import { ReactNode, useMemo } from 'react';
import * as R from 'remeda';
import humanizeDuration from 'humanize-duration';
import { useIntl } from 'react-intl';
import { Serie } from '@rossum/api-client/annotations';
import { useTheme } from '@rossum/ui/material';
import { BarChart } from '@rossum/ui/x-charts-pro';
import { humanizeDurationLocaleMap } from '../../../../i18n';
import { fallbackLocale } from '../../../../i18n/config';
import {
  dateFormatByGranularity,
  getDurationUnit,
  limitUnits,
  shortenTimes,
} from '../../../../lib/timeUtils';
import EmptySeries from '../../components/EmptySeries';
import GraphContainer from '../../components/GraphContainer';
import { GranularityT } from '../Granularity';

const BAR_CHART_HEIGHT = 280;

type Props = {
  isTmpState: boolean;
  series: Array<Serie>;
  children: ReactNode;
  total: number;
  groupBy: GranularityT;
};

const TimeGraph = ({ isTmpState, series, children, total, groupBy }: Props) => {
  const intl = useIntl();
  const theme = useTheme();

  const data = useMemo(
    () =>
      series.map(({ values: { turnaroundAvgS }, beginDate, endDate }) => ({
        value: R.round(turnaroundAvgS || 0, 2),
        date: dateFormatByGranularity(intl.locale, groupBy, beginDate, endDate),
      })),
    [series, intl.locale, groupBy]
  );

  const displayPlaceholder = data.every(d => d.value === 0);

  const maxDurationUnit = getDurationUnit(total ?? 0);

  const formatTimeShorten = useMemo(
    () => shortenTimes(intl.locale, maxDurationUnit),
    [intl.locale, maxDurationUnit]
  );

  const formatTime = useMemo(
    () => (seconds: number) =>
      humanizeDuration(seconds * 1000, {
        largest: 2,
        round: true,
        language: intl.locale,
        fallbacks: [
          humanizeDurationLocaleMap[intl.locale] ?? fallbackLocale,
          fallbackLocale,
        ],
        units: limitUnits(maxDurationUnit),
      }),
    [intl.locale, maxDurationUnit]
  );

  return (
    <GraphContainer graphName="turnaroundAvgS">
      <span style={{ opacity: displayPlaceholder ? 0.1 : 1 }}>{children}</span>
      {displayPlaceholder ? (
        <EmptySeries inGraph graphName="turnaroundAvgS" />
      ) : (
        <BarChart
          skipAnimation
          height={BAR_CHART_HEIGHT}
          series={[
            {
              data: data.map(d => d.value),
              color: isTmpState
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
              valueFormatter: v => (v != null ? formatTime(v) : ''),
            },
          ]}
          xAxis={[
            {
              data: data.map((_, i) => i),
              scaleType: 'band',
              valueFormatter: (v: number) => data[v]?.date ?? '',
            },
          ]}
          yAxis={[
            {
              min: 0,
              tickNumber: 5,
              tickMinStep: 60,
              valueFormatter: formatTimeShorten,
            },
          ]}
          grid={{ horizontal: true }}
          hideLegend
        />
      )}
    </GraphContainer>
  );
};

export default TimeGraph;
